import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import classNames from 'classnames';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import AuthService from '@/shared/services/AuthService';
import DataCacheService from '@/shared/services/DataCacheService';
import Button from '@/shared/components/Button';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { CircleCheckIcon, CancelIcon } from '@/shared/components/Icons';
import FormattedDateShort from '@/shared/components/FormattedDateShort';
import DatePicker from '@/shared/components/input/DatePicker';
import UserSearch from '@/shared/components/input/UserSearch';
import { falseFunc } from '@/shared/utils/validationHelpers';
import messages from './interactionMessages';
import './ToDoSuggestionsModal.css';
import { getFirstSelectedInvoiceStreamId } from '@/shared/components/registration/registrationHelpers';
import useGetRelation from '@/shared/hooks/relation/useGetRelation';

const ToDoSuggestionsModal = ({
  onSave,
  onClose,
  show = false,
  relationId = null,
  interaction,
  suggestions: propSuggestions,
  loading = false,
}) => {
  const intl = useIntl();

  // Initialize suggestions with accept/reject state
  const [suggestions, setSuggestions] = useState(() =>
    propSuggestions.map((suggestion, index) => ({
      ...suggestion,
      id: index,
      status: 'pending', // 'pending', 'accepted', 'rejected'
    })),
  );

  const [processing, setProcessing] = useState(false);
  const [focusedDatePickerId, setFocusedDatePickerId] = useState(null);
  const relation = useGetRelation(relationId);

  // Update suggestions when props change
  useEffect(() => {
    if (!loading) {
      setSuggestions(
        propSuggestions.map((suggestion, index) => ({
          ...suggestion,
          id: index,
          status: 'pending',
        })),
      );
    }
  }, [propSuggestions, loading]);

  const getUserDisplayName = useCallback(
    userId => {
      if (!userId) return intl.formatMessage(messages.Unassigned);
      const userName = DataCacheService.getUserName(userId);
      return userName || intl.formatMessage(messages.Unassigned);
    },
    [intl],
  );

  const handleSuggestionAction = useCallback((suggestionId, action) => {
    setSuggestions(prevSuggestions =>
      prevSuggestions.map(suggestion =>
        suggestion.id === suggestionId ? { ...suggestion, status: action } : suggestion,
      ),
    );
  }, []);

  const handleDeadlineChange = useCallback((suggestionId, deadline) => {
    setSuggestions(prevSuggestions =>
      prevSuggestions.map(suggestion =>
        suggestion.id === suggestionId
          ? { ...suggestion, deadline: moment(deadline).format('YYYY-MM-DD') }
          : suggestion,
      ),
    );
  }, []);

  const handleAssignedToChange = useCallback((suggestionId, user) => {
    setSuggestions(prevSuggestions =>
      prevSuggestions.map(suggestion =>
        suggestion.id === suggestionId
          ? { ...suggestion, assignedTo: user ? user.id : null }
          : suggestion,
      ),
    );
  }, []);

  const handleTaskChange = useCallback((suggestionId, task) => {
    setSuggestions(prevSuggestions =>
      prevSuggestions.map(suggestion =>
        suggestion.id === suggestionId ? { ...suggestion, task: task } : suggestion,
      ),
    );
  }, []);

  const handleRemarkChange = useCallback((suggestionId, remark) => {
    setSuggestions(prevSuggestions =>
      prevSuggestions.map(suggestion =>
        suggestion.id === suggestionId ? { ...suggestion, remark: remark } : suggestion,
      ),
    );
  }, []);

  const handleDatePickerFocusChange = useCallback((suggestionId, { focused }) => {
    setFocusedDatePickerId(focused ? suggestionId : null);
  }, []);

  const handleAcceptAll = useCallback(() => {
    setSuggestions(prevSuggestions =>
      prevSuggestions.map(suggestion => ({ ...suggestion, status: 'accepted' })),
    );
  }, []);

  const handleRejectAll = useCallback(() => {
    setSuggestions(prevSuggestions =>
      prevSuggestions.map(suggestion => ({ ...suggestion, status: 'rejected' })),
    );
  }, []);

  const processSuggestions = useCallback(async () => {
    setProcessing(true);

    const acceptedSuggestions = suggestions.filter(s => s.status === 'accepted');
    const rejectedCount = suggestions.filter(s => s.status === 'rejected').length;

    if (acceptedSuggestions.length === 0) {
      ToastrService.info(
        intl.formatMessage(messages.SuggestionsProcessed, {
          accepted: 0,
          rejected: rejectedCount,
        }),
      );
      onClose();
      return;
    }

    if (!relation || !relation.invoiceStreamId) {
      ToastrService.error(intl.formatMessage(messages.RelationRequiredForTodo));
      setProcessing(false);
      return;
    }

    try {
      const createdTodos = [];

      // Create each accepted todo
      for (const suggestion of acceptedSuggestions) {
        const task = {
          taskLevel: 2, // 2 = to do
          name: suggestion.task,
          remark: suggestion.remark || '',
          deadline: moment.utc(suggestion.deadline),
          userId: suggestion.assignedTo || null,
          relation: relation,
          relationId: relation?.id,
          invoiceStreamId: getFirstSelectedInvoiceStreamId(relation),
        };

        try {
          const { data: todo } = await api.base.post(
            `${config.appUrls.tasks}/api/plannedTasks`,
            task,
          );

          createdTodos.push(todo);

          // Link to interaction if provided
          if (todo.id && interaction) {
            const interactionLink = {
              interactionId: interaction.id,
              linkId: todo.id,
              linkType: 1,
            };
            await api.base.post(
              `${config.appUrls.interactions}/api/interactions/links/create`,
              interactionLink,
            );
          }
        } catch (error) {
          console.error('Failed to create todo:', error);
          handleHttpError(intl)(error);
        }
      }

      ToastrService.success(
        intl.formatMessage(messages.SuggestionsProcessed, {
          accepted: createdTodos.length,
          rejected: rejectedCount,
        }),
      );

      onSave(createdTodos);
    } catch (error) {
      handleHttpError(intl)(error);
    } finally {
      setProcessing(false);
    }
  }, [suggestions, intl, relation, interaction, onSave, onClose]);

  const closeModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const acceptedCount = suggestions.filter(s => s.status === 'accepted').length;
  const rejectedCount = suggestions.filter(s => s.status === 'rejected').length;
  const pendingCount = suggestions.filter(s => s.status === 'pending').length;

  const hasActions = acceptedCount > 0 || rejectedCount > 0;

  return (
    <OkCancelModal
      title={intl.formatMessage(messages.GeneratedToDoSuggestions)}
      show={show}
      onClose={closeModal}
      disabled={loading || processing || !hasActions || !relation}
      onSave={processSuggestions}
      okText={
        loading
          ? intl.formatMessage(messages.GeneratingToDoSuggestions)
          : processing
          ? intl.formatMessage(messages.ProcessingSuggestions)
          : undefined
      }
      cancelDisabled={loading}
      size="xl"
      className="todo-suggestions-modal"
    >
      {show && (
        <div className="container-fluid">
          {loading ? (
            <div className="text-center margin20T margin20B">
              <i className="fa fa-spin fa-spinner fa-2x margin10B" />
              <div>
                <FormattedMessage {...messages.GeneratingToDoSuggestions} />
              </div>
              <div className="text-muted margin5T">
                <FormattedMessage {...messages.Loading} />
              </div>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-center margin20T margin20B">
              <FormattedMessage {...messages.NoSuggestionsGenerated} />
            </div>
          ) : (
            <React.Fragment>
              {/* Bulk Actions */}
              <div className="row margin10B">
                <div className="col-sm-12">
                  <div className="pull-right">
                    <Button
                      btnClassName="btn btn-outline-success btn-sm margin5R bulk-accept-btn"
                      onClick={handleAcceptAll}
                      disabled={processing}
                    >
                      <FaCheck className="margin5R" />
                      <FormattedMessage {...messages.AcceptAll} />
                    </Button>
                    <Button
                      btnClassName="btn btn-outline-danger btn-sm bulk-reject-btn"
                      onClick={handleRejectAll}
                      disabled={processing}
                    >
                      <FaTimes className="margin5R" />
                      <FormattedMessage {...messages.RejectAll} />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Suggestions Table */}
              <div className="todo-suggestions-table-container">
                <table className="table table-grid table-hover registration-table todo-suggestions-table">
                  <thead>
                    <tr>
                      <th width="35%">
                        <FormattedMessage {...messages.Task} />
                      </th>
                      <th width="15%">
                        <FormattedMessage {...messages.Deadline} />
                      </th>
                      <th width="25%">
                        <FormattedMessage {...messages.AssignedTo} />
                      </th>
                      <th width="20%">
                        <FormattedMessage {...messages.Remark} />
                      </th>
                      <th width="5%" className="text-right">
                        <FormattedMessage {...messages.Actions} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestions.map(suggestion => (
                      <tr key={suggestion.id}>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={suggestion.task}
                            onChange={e => handleTaskChange(suggestion.id, e.target.value)}
                            placeholder={intl.formatMessage(messages.Task)}
                            disabled={processing}
                          />
                        </td>
                        <td>
                          <DatePicker
                            placeholder={intl.formatMessage(messages.Deadline)}
                            date={moment(suggestion.deadline)}
                            onDateChange={deadline => handleDeadlineChange(suggestion.id, deadline)}
                            focused={focusedDatePickerId === suggestion.id}
                            onFocusChange={focusState =>
                              handleDatePickerFocusChange(suggestion.id, focusState)
                            }
                            id={`deadline-${suggestion.id}`}
                            block
                            isOutsideRange={falseFunc}
                            disabled={processing}
                          />
                        </td>
                        <td>
                          <UserSearch
                            onChange={user => handleAssignedToChange(suggestion.id, user)}
                            value={suggestion.assignedTo}
                            hideClearButton={false}
                            disabled={processing}
                            placeholder={intl.formatMessage(messages.Unassigned)}
                            small
                          />
                        </td>
                        <td>
                          <textarea
                            className="form-control input-sm vertical-resize"
                            value={suggestion.remark || ''}
                            onChange={e => handleRemarkChange(suggestion.id, e.target.value)}
                            placeholder={intl.formatMessage(messages.Remark)}
                            disabled={processing}
                            rows={1}
                            style={{ minHeight: '34px' }}
                          />
                        </td>
                        <td className="text-right actions-column">
                          <a
                            className={classNames('btn btn-xs btn-icon', {
                              accepted: suggestion.status === 'accepted',
                            })}
                            title={intl.formatMessage(messages.AcceptSuggestion)}
                            onClick={() => handleSuggestionAction(suggestion.id, 'accepted')}
                            disabled={processing}
                          >
                            <CircleCheckIcon className="file-icon" />
                          </a>
                          <a
                            className={classNames('btn btn-xs btn-icon', {
                              rejected: suggestion.status === 'rejected',
                            })}
                            title={intl.formatMessage(messages.RejectSuggestion)}
                            onClick={() => handleSuggestionAction(suggestion.id, 'rejected')}
                            disabled={processing}
                          >
                            <CancelIcon className="file-icon" />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Status Summary */}
              {hasActions && (
                <div className="row margin10T">
                  <div className="col-sm-12">
                    <div className="text-muted">
                      {acceptedCount > 0 && (
                        <span className="margin10R">
                          <FaCheck className="text-success margin5R" />
                          {acceptedCount} accepted
                        </span>
                      )}
                      {rejectedCount > 0 && (
                        <span className="margin10R">
                          <FaTimes className="text-danger margin5R" />
                          {rejectedCount} rejected
                        </span>
                      )}
                      {pendingCount > 0 && <span>{pendingCount} pending</span>}
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      )}
    </OkCancelModal>
  );
};

ToDoSuggestionsModal.propTypes = {
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  show: PropTypes.bool,
  relationId: PropTypes.string.isRequired,
  interaction: PropTypes.object.isRequired,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      task: PropTypes.string.isRequired,
      remark: PropTypes.string,
      deadline: PropTypes.string.isRequired, // YYYY-MM-DD format
      assignedTo: PropTypes.string, // userId
    }),
  ).isRequired,
  loading: PropTypes.bool,
};

export default ToDoSuggestionsModal;
